import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiChevronDown,
  FiInfo,
  FiPlus,
  FiShield,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { LuFileText } from "react-icons/lu";
import { ensurePayoutsAccessToken, isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import { availableBalanceRupeesFromWire } from "./api/payoutMoney";
import {
  clearTransferSession,
  newTransferClientReferenceId,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import {
  accountFromApiRecord,
  accountToTransferSelectOption,
  beneficiaryFromApiRecord,
  beneficiaryToTransferSelectOption,
} from "./api/payoutApiAdapters";
import type { PayoutAccount } from "./payoutData";
import { usePayoutResource } from "./api/usePayoutResource";
import { showPayoutNotification } from "./PayoutNotification";
import { contextualValidationMessage } from "./payoutAlertMessages";

const formatINR = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const lowValueTransferMethods = ["NEFT", "IMPS"];
const highValueTransferMethods = ["RTGS"];
const rtgsThresholdAmount = 200000;

type SelectOption = {
  title: string;
  description?: string;
  id?: string;
  funding_account_id?: string;
  accountId?: string;
  beneficiary_id?: string;
  availableBalance?: number;
};

const categoryOptions: SelectOption[] = [
  { title: "Vendor" },
  { title: "Salary" },
  { title: "Scholarship" },
  { title: "Refund" },
  { title: "Reimbursement" },
  { title: "Others" },
];

function Step({
  number,
  label,
  active = false,
}: {
  number: number;
  label: string;
  active?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold " +
          (active
            ? "border-[#1B163B] bg-[#1B163B] text-white"
            : "border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {number}
      </span>
      <span
        className={
          "text-sm font-semibold " +
          (active ? "text-[#1B163B]" : "text-[#505E8E]")
        }
      >
        {label}
      </span>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
      {children}
    </span>
  );
}

function FieldError({ children }: { children?: string }) {
  if (!children) return null;
  return <p className="mt-2 text-xs font-semibold text-[#EF4444]">{children}</p>;
}

function SelectField({
  label,
  options,
  value,
  onChange,
  active = false,
  error,
  placeholder = "Loading…",
}: {
  label: string;
  options: SelectOption[];
  value?: SelectOption | null;
  onChange: (value: SelectOption) => void;
  active?: boolean;
  error?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const resolvedValue = value ?? {
    title: placeholder,
    description: options.length ? undefined : "Waiting for payouts API",
  };

  return (
    <div className="relative">
      <FieldLabel>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => {
          if (!options.length || !value) return;
          setOpen((current) => !current);
        }}
        disabled={!options.length || !value}
        className={
          "flex h-[58px] w-full items-center justify-between rounded-[6px] border bg-white px-4 text-left " +
          (error
            ? "border-[#EF4444]"
            : active || open
              ? "border-[#1B163B]"
              : "border-[#E0E5F2]") +
          (!options.length || !value ? " cursor-not-allowed opacity-70" : "")
        }
      >
        <span>
          <span className="block text-sm font-semibold text-[#1B163B]">
            {resolvedValue.title}
          </span>
          {resolvedValue.description && (
            <span className="mt-1 block text-xs font-medium text-[#505E8E]">
              {resolvedValue.description}
            </span>
          )}
        </span>
        <FiChevronDown
          className={
            "text-[#505E8E] " + (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
          {options.map((option) => (
            <button
              type="button"
              key={option.title}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span>
                <span className="block text-sm font-semibold text-[#1B163B]">
                  {option.title}
                </span>
                {option.description && (
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {option.description}
                  </span>
                )}
              </span>
              {option.title === resolvedValue.title && (
                <span className="h-2 w-2 rounded-full bg-[#1B163B]" />
              )}
            </button>
          ))}
        </div>
      )}
      <FieldError>{error}</FieldError>
    </div>
  );
}

function RadioDot({ active = false }: { active?: boolean }) {
  return (
    <span
      className={
        "flex h-5 w-5 items-center justify-center rounded-full border " +
        (active ? "border-[#1B163B]" : "border-[#A3AED0]")
      }
    >
      {active && <span className="h-2.5 w-2.5 rounded-full bg-[#1B163B]" />}
    </span>
  );
}

function SummaryRow({
  label,
  children,
  strong = false,
}: {
  label: string;
  children: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-6 py-2.5">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div
        className={
          "max-w-[190px] text-right text-sm " +
          (strong
            ? "font-semibold text-[#1B163B]"
            : "font-medium text-[#1B163B]")
        }
      >
        {children}
      </div>
    </div>
  );
}

function PayoutInitiateTransfer() {
  const navigate = useNavigate();
  const emptyPayoutAccount: PayoutAccount = {
    id: "",
    name: "",
    school: "",
    bank: "",
    logoUrl: "",
    accountNumber: "",
    ifsc: "",
    currency: "INR",
    addedOn: "",
    availableBalance: 0,
    accountBalance: 0,
    fundsOnHold: 0,
    overdraftBalance: 0,
    overdraftLimit: 0,
    lowBalanceThreshold: 0,
    accent: "bg-[#6687FF]",
  };
  const accountsResource = usePayoutResource(
    [] as PayoutAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((accounts) =>
        accounts.map((account) => accountFromApiRecord(account, emptyPayoutAccount)),
      ),
  );
  const beneficiariesResource = usePayoutResource(
    [] as ReturnType<typeof beneficiaryFromApiRecord>[],
    (signal) =>
      payoutApi
        .beneficiaries({ limit: 100, verification_status: "VERIFIED" }, signal)
        .then((rows) => rows.map((row) => beneficiaryFromApiRecord(row, {}))),
  );
  const accountOptions = useMemo(() => {
    return accountsResource.data.map(accountToTransferSelectOption);
  }, [accountsResource.data]);
  const beneficiaryOptions = useMemo(() => {
    return beneficiariesResource.data.map(beneficiaryToTransferSelectOption);
  }, [beneficiariesResource.data]);
  const apiTransferReady =
    isPayoutApiMode() &&
    !accountsResource.loading &&
      !beneficiariesResource.loading &&
      !accountsResource.error &&
      !beneficiariesResource.error &&
      !accountsResource.usingFallback &&
      !beneficiariesResource.usingFallback &&
      accountOptions.length > 0 &&
      beneficiaryOptions.length > 0;

  useEffect(() => {
    clearTransferSession();
    if (!isPayoutApiMode()) return;
    ensurePayoutsAccessToken().catch((err) => {
      console.warn("Payouts auth exchange failed on initiate transfer", err);
    });
  }, []);
  const cancelTransfer = () => {
    clearTransferSession();
    navigate("/payouts/transfers");
  };
  const [selectedAccount, setSelectedAccount] = useState<SelectOption | null>(null);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<SelectOption | null>(null);
  const [selectedCategory, setSelectedCategory] = useState(categoryOptions[0]);
  const [selectedMethod, setSelectedMethod] = useState("IMPS");
  const [schedule, setSchedule] = useState("Now");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [amount, setAmount] = useState("0.00");
  const [remarks, setRemarks] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [liveBalance, setLiveBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const amountValue = Number(amount.replace(/,/g, "")) || 0;
  const availableTransferMethods = useMemo(
    () =>
      amountValue > rtgsThresholdAmount
        ? highValueTransferMethods
        : lowValueTransferMethods,
    [amountValue],
  );
  const availableBalance =
    liveBalance ??
    selectedAccount?.availableBalance ??
    accountsResource.data.find((item) => item.id === selectedAccount?.id)?.availableBalance ??
    0;

  useEffect(() => {
    if (!accountOptions.length) return;
    setSelectedAccount((current) =>
      current && accountOptions.some((item) => item.id === current.id)
        ? current
        : accountOptions[0],
    );
  }, [accountOptions]);

  useEffect(() => {
    if (!beneficiaryOptions.length) return;
    setSelectedBeneficiary((current) =>
      current && beneficiaryOptions.some((item) => item.id === current.id)
        ? current
        : beneficiaryOptions[0],
    );
  }, [beneficiaryOptions]);

  useEffect(() => {
    const accountId = selectedAccount?.id;
    if (!accountId || !isPayoutApiMode() || accountsResource.usingFallback) {
      setLiveBalance(null);
      setBalanceLoading(false);
      return;
    }
    let cancelled = false;
    setBalanceLoading(true);
    payoutApi
      .accountBalance(accountId)
      .then((payload) => {
        if (cancelled) return;
        setLiveBalance(availableBalanceRupeesFromWire(payload));
      })
      .catch(() => {
        if (!cancelled) setLiveBalance(null);
      })
      .finally(() => {
        if (!cancelled) setBalanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountsResource.usingFallback, selectedAccount?.id]);

  const clearError = (field: string) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  useEffect(() => {
    if (availableTransferMethods.includes(selectedMethod)) return;
    setSelectedMethod(
      availableTransferMethods.includes("IMPS")
        ? "IMPS"
        : availableTransferMethods[0],
    );
    clearError("method");
  }, [availableTransferMethods, selectedMethod]);

  const getScheduledDateTimeLabel = () => {
    if (schedule !== "Schedule for Later") return "Now";
    if (!scheduleDate || !scheduleTime) return "-";

    const selectedDate = new Date(`${scheduleDate}T${scheduleTime}`);
    if (Number.isNaN(selectedDate.getTime())) return "-";

    return selectedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const validateTransfer = () => {
    const nextErrors: Record<string, string> = {};
    const normalizedAmount = amount.replace(/,/g, "").trim();
    const numericAmount = Number(normalizedAmount);

    if (!selectedAccount?.title) {
      nextErrors.account = "Select payout account.";
    }
    if (!selectedBeneficiary?.title) {
      nextErrors.beneficiary = "Select beneficiary.";
    }
    if (!normalizedAmount) {
      nextErrors.amount = "Enter transfer amount.";
    } else if (!/^\d+(\.\d{1,2})?$/.test(normalizedAmount)) {
      nextErrors.amount = "Enter a valid amount with up to 2 decimals.";
    } else if (numericAmount <= 0) {
      nextErrors.amount = "Transfer amount must be greater than zero.";
    } else if (numericAmount > availableBalance) {
      if (balanceLoading) {
        nextErrors.amount = "Waiting for account balance. Try again in a moment.";
      } else if (availableBalance <= 0 && isPayoutApiMode()) {
        nextErrors.amount = "Unable to read available balance for this account.";
      } else {
        nextErrors.amount = "Amount cannot exceed available balance.";
      }
    }
    if (!selectedCategory?.title) {
      nextErrors.category = "Select transfer category.";
    }
    if (!selectedMethod || !availableTransferMethods.includes(selectedMethod)) {
      nextErrors.method = "Select transfer method.";
    }
    if (schedule === "Schedule for Later") {
      if (!scheduleDate) {
        nextErrors.scheduleDate = "Select schedule date.";
      }
      if (!scheduleTime) {
        nextErrors.scheduleTime = "Select schedule time.";
      }
      if (scheduleDate && scheduleTime) {
        const selectedDate = new Date(`${scheduleDate}T${scheduleTime}`);
        if (Number.isNaN(selectedDate.getTime())) {
          nextErrors.scheduleDate = "Select a valid schedule date and time.";
        } else if (selectedDate <= new Date()) {
          nextErrors.scheduleDate = "Schedule date and time must be in the future.";
        }
      }
    }
    if (remarks.length > 150) {
      nextErrors.remarks = "Remarks cannot exceed 150 characters.";
    }
    if (uploadedFile) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!allowedTypes.includes(uploadedFile.type)) {
        nextErrors.uploadedFile = "Upload a PDF, JPG or PNG invoice.";
      } else if (uploadedFile.size > 5 * 1024 * 1024) {
        nextErrors.uploadedFile = "Invoice file must be under 5 MB.";
      }
    }

    setErrors(nextErrors);

    if (!Object.keys(nextErrors).length) {
      if (isPayoutApiMode() && !apiTransferReady) {
        nextErrors.account = "Payout accounts are not loaded from the API. Re-open /admin?token=… and check payouts-service on :4100.";
        setErrors(nextErrors);
        return;
      }
      writeSessionJson("payoutTransferClientReferenceId", newTransferClientReferenceId());
      sessionStorage.setItem(
        "payoutTransferDraft",
        JSON.stringify({
          account: selectedAccount,
          beneficiary: selectedBeneficiary,
          purpose: remarks.trim() || "-",
          category: selectedCategory.title,
          method: selectedMethod,
          schedule,
          scheduleDate,
          scheduleTime,
          scheduledFor: getScheduledDateTimeLabel(),
          amount: numericAmount,
          remarks,
          uploadedFile: uploadedFile
            ? {
                name: uploadedFile.name,
                size: Math.max(1, Math.round(uploadedFile.size / 1024)),
              }
            : null,
        }),
      );
      navigate("/payouts/transfers/initiate/review");
    } else {
      showPayoutNotification({
        tone: "error",
        message: contextualValidationMessage(
          "Initiate transfer",
          nextErrors,
          "Initiate transfer: complete the highlighted transfer details before proceeding.",
        ),
      });
    }
  };

  useEffect(() => {
    const apiError = accountsResource.error || beneficiariesResource.error;
    if (!isPayoutApiMode() || !apiError) return;
    showPayoutNotification({
      tone: "error",
      message: `Payouts API error: ${apiError}.`,
    });
  }, [accountsResource.error, beneficiariesResource.error]);

  useEffect(() => {
    if (
      !isPayoutApiMode() ||
      accountsResource.loading ||
      beneficiariesResource.loading ||
      accountsResource.error ||
      beneficiariesResource.error ||
      apiTransferReady
    ) {
      return;
    }
    showPayoutNotification({
      tone: "error",
      message:
        "No verified beneficiaries or funding accounts loaded from the API. Cannot create a real transfer until data loads.",
    });
  }, [
    accountsResource.loading,
    beneficiariesResource.loading,
    accountsResource.error,
    beneficiariesResource.error,
    apiTransferReady,
  ]);

  const resolvedAccount = selectedAccount ?? accountOptions[0] ?? null;
  const resolvedBeneficiary = selectedBeneficiary ?? beneficiaryOptions[0] ?? null;

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#000000]">
            Initiate Transfer
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Create a new payout transfer
          </p>
        </div>
      </div>

      {false && (
        <div className="mb-4 rounded-[8px] border border-[#F5C2C7] bg-[#FFF5F5] px-4 py-3 text-sm font-semibold text-[#9F1D1D]">
          Payouts API error: {accountsResource.error || beneficiariesResource.error}. Re-open{" "}
          <code className="text-xs">/admin?token=…</code> and ensure payouts-service is running on{" "}
          <code className="text-xs">:4100</code>.
        </div>
      )}
      {false &&
        !accountsResource.loading &&
        !beneficiariesResource.loading &&
        !accountsResource.error &&
        !beneficiariesResource.error &&
        !apiTransferReady && (
          <div className="mb-4 rounded-[8px] border border-[#F5C2C7] bg-[#FFF5F5] px-4 py-3 text-sm font-semibold text-[#9F1D1D]">
            No verified beneficiaries or funding accounts loaded from the API. Cannot create a real
            transfer until data loads.
          </div>
        )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 border-y border-[#E0E5F2] bg-white px-2 py-5">
        <div className="flex flex-wrap items-center gap-16">
          <Step number={1} label="Transfer Details" active />
          <Step number={2} label="Review" />
          <Step number={3} label="Verify" />
          <Step number={4} label="Confirm" />
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={cancelTransfer}
            className="flex h-10 min-w-[120px] items-center justify-center rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={validateTransfer}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="flex h-10 min-w-[190px] items-center justify-center gap-4 rounded-[6px] px-8 text-sm font-semibold shadow-sm"
          >
            Save & Continue
            <FiArrowRight className="text-lg" />
          </button>
        </div>
      </div>

      {false && (
        <div className="mb-5 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-5 py-4 text-sm font-semibold text-[#EF4444]">
          Initiate transfer: complete the highlighted transfer details before proceeding.
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_430px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-7 text-[18px] font-semibold text-[#1B163B]">
            Transfer Details
          </h2>

          <div className="grid gap-x-8 gap-y-7 lg:grid-cols-2">
            <SelectField
              label="Select Account"
              options={accountOptions}
              value={selectedAccount ?? accountOptions[0] ?? null}
              onChange={(value) => {
                setSelectedAccount(value);
                setLiveBalance(null);
                clearError("account");
              }}
              error={errors.account}
            />

            <div className="flex items-end gap-4">
              <div className="flex-1">
                <SelectField
                  label="Beneficiary"
                  options={beneficiaryOptions}
                  value={selectedBeneficiary ?? beneficiaryOptions[0] ?? null}
                  onChange={(value) => {
                    setSelectedBeneficiary(value);
                    clearError("beneficiary");
                  }}
                  error={errors.beneficiary}
                />
              </div>
              <Link
                to="/payouts/beneficiaries/add"
                className="mb-3 flex h-9 shrink-0 items-center gap-2 rounded-[6px] border border-[#1B163B] bg-white px-4 text-sm font-semibold text-[#1B163B]"
              >
                <FiPlus />
                Add New
              </Link>
            </div>

            <label className="block">
              <FieldLabel>Amount</FieldLabel>
              <div
                className={
                  "flex h-[52px] items-center gap-4 rounded-[6px] border bg-white px-4 " +
                  (errors.amount ? "border-[#EF4444]" : "border-[#E0E5F2]")
                }
              >
                <span className="text-base font-semibold text-[#505E8E]">
                  {"\u20B9"}
                </span>
                <input
                className="w-full bg-transparent text-sm font-semibold text-[#1B163B] outline-none"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value.replace(/[^\d.,]/g, ""));
                  clearError("amount");
                }}
                onBlur={() => {
                  const normalizedAmount = amount.replace(/,/g, "").trim();
                  const numericAmount = Number(normalizedAmount);
                  setAmount(
                    normalizedAmount && Number.isFinite(numericAmount)
                      ? numericAmount.toFixed(2)
                      : "0.00",
                  );
                }}
              />
              </div>
              <FieldError>{errors.amount}</FieldError>
              <p className="mt-3 text-xs font-medium text-[#505E8E]">
                Available Balance:{" "}
                {balanceLoading
                  ? "Loading…"
                  : formatINR(availableBalance)}
              </p>
            </label>

            <SelectField
              label="Category"
              options={categoryOptions}
              value={selectedCategory}
              onChange={(value) => {
                setSelectedCategory(value);
                clearError("category");
              }}
              active
              error={errors.category}
            />

            <div>
              <FieldLabel>Transfer Method</FieldLabel>
              <div className="flex min-h-[52px] flex-wrap items-center gap-8">
                {availableTransferMethods.map((method) => (
                  <label
                    key={method}
                    className="flex items-center gap-3 text-sm font-semibold text-[#1B163B]"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMethod(method);
                        clearError("method");
                      }}
                    >
                      <RadioDot active={method === selectedMethod} />
                    </button>
                    {method}
                  </label>
                ))}
              </div>
              <FieldError>{errors.method}</FieldError>
            </div>

            <div>
              <FieldLabel>Schedule Transfer</FieldLabel>
              <div className="flex min-h-[52px] flex-wrap items-center gap-10">
                <label className="flex items-center gap-3 text-sm font-semibold text-[#1B163B]">
                  <button
                    type="button"
                    onClick={() => {
                      setSchedule("Now");
                      clearError("scheduleDate");
                      clearError("scheduleTime");
                    }}
                  >
                    <RadioDot active={schedule === "Now"} />
                  </button>
                  Now
                </label>
                <label className="flex items-center gap-3 text-sm font-semibold text-[#1B163B]">
                  <button
                    type="button"
                    onClick={() => setSchedule("Schedule for Later")}
                  >
                    <RadioDot active={schedule === "Schedule for Later"} />
                  </button>
                  Schedule for Later
                </label>
              </div>
              {schedule === "Schedule for Later" && (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <FieldLabel>Schedule Date</FieldLabel>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(event) => {
                        setScheduleDate(event.target.value);
                        clearError("scheduleDate");
                      }}
                      className={
                        "h-11 w-full rounded-[6px] border bg-white px-3 text-sm font-semibold text-[#1B163B] outline-none " +
                        (errors.scheduleDate ? "border-[#EF4444]" : "border-[#E0E5F2]")
                      }
                    />
                    <FieldError>{errors.scheduleDate}</FieldError>
                  </label>
                  <label className="block">
                    <FieldLabel>Schedule Time</FieldLabel>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(event) => {
                        setScheduleTime(event.target.value);
                        clearError("scheduleTime");
                      }}
                      className={
                        "h-11 w-full rounded-[6px] border bg-white px-3 text-sm font-semibold text-[#1B163B] outline-none " +
                        (errors.scheduleTime ? "border-[#EF4444]" : "border-[#E0E5F2]")
                      }
                    />
                    <FieldError>{errors.scheduleTime}</FieldError>
                  </label>
                </div>
              )}
            </div>

            <label className="block">
              <FieldLabel>
                Reference / Remarks{" "}
                <span className="font-medium text-[#717171]">(Optional)</span>
              </FieldLabel>
              <input
                className="h-[52px] w-full rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B] outline-none"
                value={remarks}
                onChange={(event) => {
                  setRemarks(event.target.value.slice(0, 150));
                  clearError("remarks");
                }}
              />
              <p className="mt-3 text-xs font-medium text-[#505E8E]">
                {remarks.length} / 150
              </p>
              <FieldError>{errors.remarks}</FieldError>
            </label>
          </div>

          <div className="my-7 h-px bg-[#E0E5F2]" />

          <div>
            <FieldLabel>
              Upload Invoice{" "}
              <span className="font-medium text-[#717171]">(Optional)</span>
            </FieldLabel>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(event) =>
                {
                  setUploadedFile(event.target.files?.[0] || null);
                  clearError("uploadedFile");
                }
              }
            />
            <div
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                setUploadedFile(event.dataTransfer.files?.[0] || null);
                clearError("uploadedFile");
              }}
              className="flex flex-wrap items-center justify-between gap-5 rounded-[6px] border border-[#E0E5F2] bg-white px-6 py-5"
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-4 text-left"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-[6px] bg-[#1B163B0D] text-[#1B163B]">
                  <FiUploadCloud className="text-2xl" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1B163B]">
                    Drag and drop file here or{" "}
                    <span className="text-[#1B163B]">click to upload</span>
                  </p>
                  <p className="mt-1 text-xs font-medium text-[#505E8E]">
                    Supported formats: PDF, JPG, PNG (Max 5MB)
                  </p>
                </div>
              </button>
              {uploadedFile && (
                <div className="flex min-w-[360px] items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-[6px] bg-[#1B163B0D] text-[#1B163B]">
                      <LuFileText />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1B163B]">
                        {uploadedFile.name}
                      </p>
                      <p className="text-xs font-medium text-[#505E8E]">
                        {Math.max(1, Math.round(uploadedFile.size / 1024))} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUploadedFile(null)}
                    className="text-[#505E8E]"
                  >
                    <FiX className="text-lg" />
                  </button>
                </div>
              )}
            </div>
            <FieldError>{errors.uploadedFile}</FieldError>
          </div>

          <div className="mt-7 flex items-center gap-4 rounded-[6px] bg-[#1B163B0D] px-5 py-4 text-sm font-medium text-[#1B163B]">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1B163B] text-white">
              <FiInfo />
            </span>
            Transfers made after 6:00 PM or on non-working days will be
            processed on the next working day.
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Transfer Summary
          </h2>
          <div className="mb-4 h-px bg-[#E0E5F2]" />

          <SummaryRow label="From Account">
            <p>{resolvedAccount?.title || "-"}</p>
            <p className="mt-1 text-[#505E8E]">{resolvedAccount?.description || "-"}</p>
          </SummaryRow>
          <SummaryRow label="Beneficiary">
            <p>{resolvedBeneficiary?.title || "-"}</p>
            <p className="mt-1 text-[#505E8E]">
              {resolvedBeneficiary?.description || "-"}
            </p>
          </SummaryRow>
          <SummaryRow label="Category">
            <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
              {selectedCategory.title}
            </span>
          </SummaryRow>
          <SummaryRow label="Transfer Method">{selectedMethod}</SummaryRow>
          <SummaryRow label="Scheduled For">{getScheduledDateTimeLabel()}</SummaryRow>
          <SummaryRow label="Transfer Amount">{formatINR(amountValue)}</SummaryRow>
          <SummaryRow label="Fees & Charges">
            <span className="inline-flex items-center gap-2">
              <FiInfo className="text-[#505E8E]" />
              {formatINR(0)}
            </span>
          </SummaryRow>

          <div className="my-5 h-px bg-[#E0E5F2]" />

          <SummaryRow label="Total Debit Amount" strong>
            {formatINR(amountValue)}
          </SummaryRow>

          <div className="mt-5 h-px bg-[#E0E5F2]" />

          <div className="mt-5 flex gap-4 rounded-[6px] bg-[#DCFCE7] px-5 py-4 text-sm font-medium text-[#1B163B]">
            <FiShield className="mt-1 shrink-0 text-lg text-[#16A34A]" />
            <p>
              This transfer will be processed securely using ClearN's trusted
              banking network.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutInitiateTransfer;
